import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Divider } from '@hivebrite/design-system/Divider';

import { renderTag } from 'hb-builder/components/BuilderComponents/dynamic/EmailList/EmailListLayouts/utils';
import type { EmailPublicationDynamicAttributes } from 'hb-builder/components/BuilderComponents/dynamic/EmailPublication/types';
import type { News } from 'hb-builder/components/BuilderComponents/dynamic/types/news';
import EmailPublicationNewsLoader from 'hb-builder/components/common/LoadingComponents/EmailPublicationNewsLoader';
import PublicationText from 'hb-react/shared/components/Publication/PublicationText';

export interface NewsWrapperProps {
  data: News;
  dynamicAttributes: EmailPublicationDynamicAttributes;
  isLoading: boolean;
}

const NewsWrapper = ({
  data,
  dynamicAttributes,
  isLoading,
}: NewsWrapperProps) => {
  if (isLoading) {
    return <EmailPublicationNewsLoader />;
  }

  const {
    displayCategories,
    displayContent,
    displayCoverPicture,
    displayTitle,
  } = dynamicAttributes.fields;

  return (
    <WrapperStyled>
      {displayCoverPicture && (
        <ImageWrapper
          alt="cover news"
          data-testid="builder-cover-wrapper"
          src={data.cover_picture}
        />
      )}
      <ContentWrapper>
        {displayTitle && (
          <WrapperTitleStyled>
            <Box mb={2}>
              <TitleStyled>{data.subject}</TitleStyled>
            </Box>
            <DateWrapper>
              {data.published_at && (
                <DateStyled>
                  {__('Published on %{date}').i__({
                    date: data.published_at,
                  })}
                </DateStyled>
              )}
            </DateWrapper>
          </WrapperTitleStyled>
        )}
        {displayCategories && Boolean(data.tags?.length) && (
          <Box fontSize="12px">
            {data.tags.map((tag, i) =>
              renderTag(tag.name, data.tags.length === i + 1),
            )}
          </Box>
        )}
        {displayContent && (
          <>
            <Divider />
            <PublicationText text={data.email_content} />
          </>
        )}
      </ContentWrapper>
    </WrapperStyled>
  );
};

export default NewsWrapper;

const DateStyled = styled(Box).attrs({
  font: 'body-regular-asphalt',
  display: 'block',
})``;

const WrapperStyled = styled(Box).attrs(() => ({
  color: 'black',
  flexDirection: 'column',
  width: '100%',
}))``;

const ImageWrapper = styled.img`
  height: 100px;
  margin: 16px 0 0 16px;
  object-fit: cover;
  position: relative;
  width: 205px;
`;

const ContentWrapper = styled(Box).attrs(() => ({
  display: 'block',
  p: 4,
}))`
  p {
    margin: 0 0 12px 0;
  }

  li p {
    display: inline-block;
    margin: 0;
  }

  ul,
  ol {
    list-style-position: inside;
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const TitleStyled = styled(Box).attrs({
  // h1-regular with no responsiveness
  color: 'black',
  fontSize: 9,
  fontWeight: 0,
  lineHeight: 9,

  display: 'block',
  mb: 1,
})``;

const WrapperTitleStyled = styled(Box).attrs({
  display: 'block',
  mb: 4,
})``;

const DateWrapper = styled(Box)`
  > div {
    color: #737373;
  }
`;
